// src/components/QuickTradeButtons.js
import React, { useState } from "react";
import api from "../utils/api";

// Who is trading?
const getUserId = () =>
    localStorage.getItem("trader_id") ||
    localStorage.getItem("email") ||
    localStorage.getItem("user_id");

// Paper vs Live selector � update this if your selector uses a different key.
const getTradeMode = () => {
    const raw = (localStorage.getItem("trade_mode") || "paper").toLowerCase();
    return raw === "live" ? "live" : "paper";
};

// Turn any error value into a readable string
function errToString(value) {
    if (!value) return "";
    if (typeof value === "string") return value;
    try {
        return JSON.stringify(value);
    } catch {
        try {
            return String(value);
        } catch {
            return "Unknown error";
        }
    }
}

/* ------------------ QUICK EQUITY TRADE (Alpaca) ------------------ */

export function QuickTradeEquityButton({ ticker }) {
    const [loading, setLoading] = useState(false);

    const handleClick = async (e) => {
        e.stopPropagation();

        const userId = getUserId();
        if (!userId) {
            alert("Please log in and connect Alpaca first.");
            return;
        }
        if (!ticker) return;

        try {
            setLoading(true);

            const mode = getTradeMode(); // "paper" or "live"

            const { data } = await api.post("/api/trade/quick/equity", {
                user_id: userId,
                symbol: ticker,
                mode, // <-- backend uses this to pick paper vs live Alpaca
            });

            if (data?.ok) {
                alert(`Equity order sent for ${ticker} (${mode.toUpperCase()}).`);
            } else {
                const raw =
                    data?.error ||
                    data?.message ||
                    data ||
                    "Order rejected by broker.";
                const msg = errToString(raw);
                alert(`Equity trade failed: ${msg}`);
            }
        } catch (err) {
            console.error("QuickTradeEquityButton error", err);
            const apiErr = err?.response?.data;
            const raw =
                apiErr?.detail ||
                apiErr?.error ||
                apiErr?.message ||
                err?.message ||
                "Request failed.";
            const msg = errToString(raw);
            alert(`Equity trade failed: ${msg}`);
        } finally {
            setLoading(false);
        }
    };

    if (!ticker) return null;

    return (
        <button
            onClick={handleClick}
            disabled={loading}
            className="mt-2 inline-flex items-center justify-center px-3 py-1.5 rounded-full bg-emerald-600 hover:bg-emerald-500 text-xs font-semibold text-white shadow disabled:opacity-60 disabled:cursor-not-allowed"
        >
            {loading ? "Sending�" : "Trade Stock"}
        </button>
    );
}

/* ------------------ QUICK OPTION TRADE (Tradier) ------------------ */

export function QuickTradeOptionButton({
    ticker,
    strategy,   // "CALL" / "PUT" / "Bullish" / "Bearish" etc.
    strike,
    expiration, // "YYYY-MM-DD"
}) {
    const [loading, setLoading] = useState(false);

    const handleClick = async (e) => {
        e.stopPropagation();

        const userId = getUserId();
        if (!userId) {
            alert("Please log in and connect Tradier first.");
            return;
        }

        if (!ticker || !strike || !expiration) {
            alert("Missing option details.");
            return;
        }

        try {
            setLoading(true);

            const mode = getTradeMode(); // "paper" | "live"

            // Map whatever your strategy label is into "call" or "put"
            const lower = (strategy || "").toLowerCase();
            const right = lower.includes("put") ? "put" : "call";

            const payload = {
                user_id: userId,
                underlying: ticker,
                expiration,
                right,
                strike,
                quantity: 1,
                mode, // <-- backend maps "paper" to Tradier sandbox, "live" to live
            };

            const { data } = await api.post(
                "/api/trade/quick/option",
                payload
            );

            if (data?.ok) {
                alert(
                    `Option order sent for ${ticker} ${right.toUpperCase()} ${strike} (${mode.toUpperCase()}).`
                );
            } else {
                const raw =
                    data?.error ||
                    data?.message ||
                    data ||
                    "Order rejected by broker.";
                const msg = errToString(raw);
                alert(`Option trade failed: ${msg}`);
            }
        } catch (err) {
            console.error("QuickTradeOptionButton error", err);
            const apiErr = err?.response?.data;
            const raw =
                apiErr?.detail ||
                apiErr?.error ||
                apiErr?.message ||
                err?.message ||
                "Request failed.";
            const msg = errToString(raw);
            alert(`Option trade failed: ${msg}`);
        } finally {
            setLoading(false);
        }
    };

    if (!ticker || !strategy || !strike || !expiration) return null;

    return (
        <button
            onClick={handleClick}
            disabled={loading}
            className="mt-2 inline-flex items-center justify-center px-3 py-1.5 rounded-full bg-purple-600 hover:bg-purple-500 text-xs font-semibold text-white shadow disabled:opacity-60 disabled:cursor-not-allowed"
        >
            {loading ? "Sending�" : "Trade Option"}
        </button>
    );
}
